/**
 * Tela do ambiente criado (spec 2026-10-09 §8.1, saída 2).
 *
 * Mostra o que o ambiente É depois de criado: como acessar (cada host com DNS,
 * TLS e HTTP, e a hora de cada leitura), serviços internos, banco, rede e
 * conta, pendências e o registro do pedido. A ficha chega filtrada pelo
 * servidor: o cliente não recebe conta, rede, state nem MR.
 *
 * "Não conferido" nunca vira ok: a tela mostra o valor do pedido COM a origem
 * dizendo de onde ele veio. 502/503 num host público é "nenhuma aplicação
 * publicada ainda" (o texto vem do servidor).
 *
 * "Conferir agora" só aparece para quem o servidor disse que pode
 * (`pode_conferir`), e o servidor decide de novo no clique.
 */
import { useParams, Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, Anchor, Badge, Button, Group, Stack, Table, Text } from '@mantine/core'
import {
  IconAlertTriangle, IconCircleCheck, IconHelpCircle, IconInfoCircle, IconRefresh,
} from '@tabler/icons-react'

import { DataCard } from '@/components/ui'
import {
  conferirFicha, fraseDaRecusa, lerFicha,
  type FichaDoAmbiente, type Leitura, type ServicoDaFicha,
} from '@/api/ambientes'
import { FONTE_DADO } from '@/pages/chat/components/Dado'

const dado = { fontFamily: FONTE_DADO }

function hora(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

// A cor do tema, resolvida: `--mantine-primary-color-filled` é o
// `inovareTeal` no `primaryShade` 7 do tema — nada de paleta própria. O
// "sem aplicação" não é erro: leva a cor da marca, e o ícone e o texto
// dizem a diferença para o ok (nunca só a cor).
const PRIMARIA = 'var(--mantine-primary-color-filled)'
const ICONE: Record<string, { Icone: typeof IconCircleCheck; cor: string }> = {
  ok: { Icone: IconCircleCheck, cor: PRIMARIA },
  sem_aplicacao: { Icone: IconInfoCircle, cor: PRIMARIA },
  erro: { Icone: IconAlertTriangle, cor: 'var(--mantine-color-red-6)' },
}
const NAO_CONFERIDO = { Icone: IconHelpCircle, cor: 'var(--mantine-color-gray-6)' }

/** Uma leitura: ícone E texto (nunca só cor), e a hora em que foi lida. */
export function EstadoDaLeitura({ leitura }: { leitura: Leitura | null }) {
  if (!leitura) return <Text size="sm" c="dimmed">não se aplica</Text>
  const { Icone, cor } = ICONE[leitura.estado] ?? NAO_CONFERIDO
  const quando = hora(leitura.lido_em)
  const texto = leitura.estado === 'nao_conferido'
    ? `não conferido${leitura.motivo ? ` (${leitura.motivo})` : ''}`
    : leitura.texto ?? leitura.estado
  return (
    <Group gap={6} wrap="nowrap" align="flex-start" data-leitura={leitura.estado}>
      <Icone size={16} color={cor} aria-hidden />
      <Stack gap={0}>
        <Text size="sm">{texto}</Text>
        {quando && <Text size="xs" c="dimmed">conferido {quando}</Text>}
      </Stack>
    </Group>
  )
}

function ComoAcessar({ servicos }: { servicos: ServicoDaFicha[] }) {
  const publicos = servicos.filter((s) => s.publico)
  if (!publicos.length) return <Text size="sm" c="dimmed">Nenhum host público.</Text>
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Host</Table.Th>
            <Table.Th>Serviço</Table.Th>
            <Table.Th>DNS</Table.Th>
            <Table.Th>TLS</Table.Th>
            <Table.Th>HTTP</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {publicos.map((s) => (
            <Table.Tr key={s.host ?? s.chave ?? ''}>
              <Table.Td><Text size="sm" style={dado}>{s.host}</Text></Table.Td>
              <Table.Td>
                <Text size="sm">{s.nome ?? s.chave}</Text>
                <Text size="xs" c="dimmed">porta <Text span size="xs" style={dado}>{s.porta}</Text></Text>
              </Table.Td>
              <Table.Td><EstadoDaLeitura leitura={s.dns} /></Table.Td>
              <Table.Td><EstadoDaLeitura leitura={s.tls} /></Table.Td>
              <Table.Td><EstadoDaLeitura leitura={s.http} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </div>
  )
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <Group gap="xs" align="baseline" wrap="nowrap">
      <Text size="sm" c="dimmed" w={140} style={{ flexShrink: 0 }}>{rotulo}</Text>
      <div>{children}</div>
    </Group>
  )
}

function Origem({ origem, estado, motivo, lidoEm }: {
  origem: string; estado?: string; motivo?: string | null; lidoEm?: string | null
}) {
  // A hora vem do `lido_em`: no texto da origem ela mudava o hash do retrato a
  // cada "Conferir agora".
  const quando = estado === 'nao_conferido' ? null : hora(lidoEm)
  return (
    <Text size="xs" c="dimmed">
      {origem}{quando ? ` · ${quando}` : ''}
      {estado === 'nao_conferido' ? ` — não conferido${motivo ? ` (${motivo})` : ''}` : ''}
    </Text>
  )
}

export function FichaDoAmbienteView({ ficha }: { ficha: FichaDoAmbiente }) {
  const internos = ficha.servicos.filter((s) => !s.publico)
  const b = ficha.banco
  return (
    <Stack gap="md">
      <DataCard title="Como acessar">
        <ComoAcessar servicos={ficha.servicos} />
      </DataCard>

      {internos.length > 0 && (
        <DataCard title="Serviços internos">
          <Stack gap={4}>
            {internos.map((s) => (
              <Text key={s.chave ?? s.nome ?? ''} size="sm">
                {s.nome ?? s.chave}: porta <Text span size="sm" style={dado}>{s.porta}</Text>
                {s.exposicao === 'kong' ? ', atrás do Kong' : ', só dentro da VPC'}
                {s.usa_banco ? ', usa o banco' : ''}
              </Text>
            ))}
          </Stack>
        </DataCard>
      )}

      <DataCard title="Banco">
        {b.engine === 'nenhum' ? (
          <Text size="sm">Sem banco.</Text>
        ) : (
          <Stack gap={4}>
            <Linha rotulo="Engine">
              <Text size="sm" style={dado}>{b.engine} {b.engine_version ?? ''}</Text>
            </Linha>
            <Linha rotulo="Classe"><Text size="sm" style={dado}>{b.instance_class}</Text></Linha>
            <Linha rotulo="Disco"><Text size="sm" style={dado}>{b.storage_gb} GB</Text></Linha>
            <Linha rotulo="Multi-AZ"><Text size="sm">{b.multi_az ? 'sim' : 'não'}</Text></Linha>
            <Origem origem={b.origem} estado={b.estado} motivo={b.motivo} lidoEm={b.lido_em} />
          </Stack>
        )}
      </DataCard>

      {(ficha.conta || ficha.rede) && (
        <DataCard title="Conta e rede">
          <Stack gap={4}>
            {ficha.conta && (
              <>
                <Linha rotulo="Conta AWS"><Text size="sm" style={dado}>{ficha.conta.account_id}</Text></Linha>
                <Linha rotulo="Região"><Text size="sm" style={dado}>{ficha.conta.regiao}</Text></Linha>
              </>
            )}
            {ficha.rede && (
              <>
                <Linha rotulo="VPC">
                  <Text size="sm" style={dado}>{ficha.rede.vpc_id ?? 'não conferida'}</Text>
                </Linha>
                <Linha rotulo="CIDR"><Text size="sm" style={dado}>{ficha.rede.cidr}</Text></Linha>
                <Origem
                  origem={ficha.rede.origem} estado={ficha.rede.estado} motivo={ficha.rede.motivo}
                  lidoEm={ficha.rede.lido_em}
                />
              </>
            )}
          </Stack>
        </DataCard>
      )}

      <DataCard title="Pendências">
        {ficha.pendencias.length ? (
          <Stack gap={4}>
            {ficha.pendencias.map((p) => <Text key={p.chave} size="sm">{p.texto}</Text>)}
          </Stack>
        ) : (
          <Text size="sm">Nenhuma pendência aberta.</Text>
        )}
      </DataCard>

      <DataCard title="Registro">
        <Stack gap={4}>
          <Linha rotulo="Pedido">
            <Text size="sm" style={dado}>#{ficha.registro.pedido}</Text>
          </Linha>
          {ficha.registro.chamado != null && (
            <Linha rotulo="Chamado">
              <Anchor component={Link} to={`/chamados/${ficha.registro.chamado}`} size="sm" style={dado}>
                #{ficha.registro.chamado}
              </Anchor>
            </Linha>
          )}
          {ficha.registro.mr && (
            <Linha rotulo="MR">
              <Anchor href={ficha.registro.mr.url} target="_blank" rel="noreferrer" size="sm" style={dado}>
                !{ficha.registro.mr.iid}
              </Anchor>
            </Linha>
          )}
          {ficha.registro.state_key && (
            <Linha rotulo="State"><Text size="sm" style={dado}>{ficha.registro.state_key}</Text></Linha>
          )}
        </Stack>
      </DataCard>
    </Stack>
  )
}

const ESTADO_DO_PEDIDO: Record<string, string> = {
  concluido: 'Entregue',
}

export default function AmbientePage() {
  const { tenantId: bruto, stageKey = '' } = useParams()
  const tenantId = Number(bruto)
  const valido = Number.isInteger(tenantId) && tenantId > 0 && stageKey !== ''
  const qc = useQueryClient()
  const chave = ['ambiente-ficha', tenantId, stageKey]
  const consulta = useQuery({
    queryKey: chave,
    queryFn: () => lerFicha(tenantId, stageKey),
    enabled: valido,
    retry: false,
  })
  const conferir = useMutation({
    mutationFn: () => conferirFicha(tenantId, stageKey),
    onSuccess: (ficha) => qc.setQueryData(chave, ficha),
  })

  if (!valido) return <Text size="sm">Endereço sem ambiente válido.</Text>
  if (consulta.isPending) return <Text size="sm" c="dimmed">Carregando a ficha do ambiente…</Text>
  if (consulta.isError) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Text size="sm">{fraseDaRecusa(consulta.error)}</Text>
      </Alert>
    )
  }
  const ficha = consulta.data
  const quando = hora(ficha.lido_em)
  return (
    <Stack gap="md" align="stretch">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={2}>
          <Group gap="xs">
            <Text fw={600} size="lg">Ambiente {ficha.ambiente} do {ficha.projeto}</Text>
            <Badge variant="light" color={ficha.estado_do_pedido === 'concluido' ? 'inovareTeal' : 'gray'}>
              {ESTADO_DO_PEDIDO[ficha.estado_do_pedido] ?? ficha.estado_do_pedido}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed" aria-live="polite">
            {quando ? `Leituras de ${quando}.` : 'Ainda sem leituras na conta e na internet.'}
            {ficha.recursos_criados ? ` ${ficha.recursos_criados} recursos criados.` : ''}
          </Text>
        </Stack>
        {ficha.pode_conferir && (
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            loading={conferir.isPending}
            onClick={() => conferir.mutate()}
          >
            Conferir agora
          </Button>
        )}
      </Group>
      {conferir.isError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">{fraseDaRecusa(conferir.error)}</Text>
        </Alert>
      )}
      <FichaDoAmbienteView ficha={ficha} />
    </Stack>
  )
}
